import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress, Toolbar } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { RootState, AppDispatch } from './store';
import { checkAuth } from './store/slices/authSlice';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './components/dashboard/Dashboard';
import Tasks from './components/tasks/Tasks';
import Companies from './components/companies/Companies';
import Individuals from './components/individuals/Individuals';
import Users from './components/users/Users';
import Profile from './components/profile/Profile';
import Categories from './components/categories/Categories';
import TaskDetailPage from './components/tasks/TaskDetailPage';
import ScheduledServices from './components/scheduledServices/ScheduledServices';
import ScheduledServiceDetail from './components/scheduledServices/ScheduledServiceDetail';
import QRCodeManagement from './components/qrcodes/QRCodeManagement';
import QRScannerNew from './components/qrcodes/QRScannerNew';
import Services from './components/Services';
import ServiceForm from './components/ServiceForm';
import ServiceDetail from './components/ServiceDetail';
import ServicePublicView from './components/ServicePublicView';
import BranchManagement from './components/branches/BranchManagement';
import UserBranchAssignment from './components/branches/UserBranchAssignment';
import CommissionReports from './components/commissions/CommissionReports';

const Reports = () => (
  <Box p={3}>
    <Typography variant="h4">Reportes</Typography>
    <Typography>Próximamente: aquí podrás ver los históricos y reportes del sistema.</Typography>
  </Box>
);

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, loading, token } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (token && !isAuthenticated && !loading) {
      dispatch(checkAuth());
    }
  }, [dispatch, token, isAuthenticated, loading]);

  if (token && loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verificando autenticación...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
            <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" />} />
            {/* Ruta pública para ver servicios vía QR */}
            <Route path="/services/public/:qrCode" element={<ServicePublicView />} />
            <Route path="/*" element={
              isAuthenticated ? (
                <Box sx={{ display: 'flex' }}>
                  <Navbar onDrawerToggle={handleDrawerToggle} />
                  <Sidebar mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} />
                  <Box
                    component="main" 
                    sx={{ 
                      flexGrow: 1, 
                      p: { xs: 2, sm: 3 },
                      width: '100%',
                      boxSizing: 'border-box',
                      overflow: 'hidden'
                    }}
                  >
                    <Toolbar />
                    <Routes>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/tasks" element={<Tasks />} />
                      <Route path="/companies" element={<Companies />} />
                      <Route path="/individuals" element={<Individuals />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/categories" element={<Categories />} />
                      <Route path="/tasks/:id" element={<TaskDetailPage />} />
                      <Route path="/scheduled-services" element={<ScheduledServices />} />
                      <Route path="/scheduled-services/:id" element={<ScheduledServiceDetail />} />
                      <Route path="/qr-codes" element={<QRCodeManagement />} />
                      <Route path="/scan-qr" element={<QRScannerNew />} />
                      <Route path="/scan-qr/:id" element={<QRScannerNew />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/services/new" element={<ServiceForm />} />
                      <Route path="/services/:id" element={<ServiceDetail />} />
                      <Route path="/services/:id/edit" element={<ServiceForm />} />
                      <Route path="/branches" element={<BranchManagement />} />
                      <Route path="/branches/assign-users" element={<UserBranchAssignment />} />
                      <Route path="/commissions" element={<CommissionReports />} />
                      <Route path="/" element={<Navigate to="/dashboard" />} />
                    </Routes>
                  </Box>
                </Box>
              ) : (
                <Navigate to="/login" />
              )
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </LocalizationProvider>
  );
};

export default App;